// Libs
import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useSelector } from 'react-redux';

// Components
import Button from '../../../Button';
import DetailsButton from '../../Buttons/DetailsButton';
import EditRecurrenceButton from '../../Buttons/EditRecurrenceButton';
import SkipButton from '../../Buttons/SkipButton';
import AddressesTruncatedLabel from '../../Labels/AddressesTruncatedLabel';

// Hooks
import useUser from '../../../../hooks/useUser';
import useUI from '../../../../hooks/useUI';
import useRecurringOrder from '../../../../pages/OrderHistory/hooks/useRecurringOrder';

// Utils
import {
  getOrderQty,
  getTotalOrderedQty,
} from '../../../../pages/OrderHistory/utils/getQuantity';
import { getFormattedNextRunDate } from '../../../../utils/date';
import getGridPropValues from '../../../../utils/getGridPropValues';

// Selectors
import { productsObjSelector } from '../../../../store/selectors';

// Styles
import { useStyles } from './RecurringOrderCardStyles';

const RecurringOrderCard = ({ order, className }) => {
  const productsObj = useSelector((state) => productsObjSelector(state));
  const { isKiteUser } = useUser();
  const { classes, cx } = useStyles({
    isKiteUser,
  });
  const { isMobileMode, isTabletMode } = useUI();
  const { onCancel, onSkip, onEditRecurrence, loadingSkip } =
    useRecurringOrder(order);
  const gridPropValues = getGridPropValues(isTabletMode, isMobileMode);

  return (
    <Grid
      container
      spacing={gridPropValues({
        lgSize: 2.5,
        mdSize: '10px',
      })}
      className={cx(className, classes.card)}>
      <Grid
        item
        flexBasis={gridPropValues({
          lgSize: 5.8,
          mdSize: 12,
          unit: '%',
        })}>
        <Grid container alignContent={isMobileMode ? 'center' : 'initial'} item>
          <Typography fontSize={{ md: 18 }} variant="h3">
            {`Next Shipping:  ${getFormattedNextRunDate(order?.next_run)}`}
          </Typography>
        </Grid>
        <Grid item md={12} className={classes.detailsBtnContainer}>
          <DetailsButton to={`${order.id}`} padding={0} />
        </Grid>
      </Grid>
      <Grid
        item
        flexBasis={gridPropValues({
          lgSize: 2.5,
          unit: '%',
        })}
        // Fixed width to properly align total ordered label in mobile mode
        // without changing grid structure
        width={isMobileMode ? '158px' : 'init'}>
        <Box display="flex" flexDirection={!isMobileMode ? 'column' : 'row'}>
          <Typography className={classes.headerText}>Order Qty:</Typography>
          <Typography className={classes.text}>{getOrderQty(order)}</Typography>
        </Box>
      </Grid>
      <Grid
        alignItems="center"
        justifyContent={isMobileMode ? 'start' : 'end'}
        item
        flexBasis={gridPropValues({
          lgSize: 3.7,
          mdSize: 12,
          unit: '%',
        })}
        container
        className={classes.buttonsContainer}
        display="flex">
        <Button className={classes.cancel} variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <SkipButton onClick={onSkip} loading={loadingSkip} />
      </Grid>
      {!isMobileMode && (
        <Grid
          item
          flexBasis={gridPropValues({
            lgSize: 5.8,
            mdSize: 12,
            unit: '%',
          })}>
          <Box display={'flex'} flexDirection={'column'}>
            <Typography className={classes.headerText}>
              Shipping Address:
            </Typography>
            <AddressesTruncatedLabel
              stringsQty={2}
              order={order}
              className={classes.text}
            />
          </Box>
        </Grid>
      )}
      <Grid
        item
        flexBasis={gridPropValues({
          lgSize: 2.5,
          unit: '%',
        })}>
        <Box display="flex" flexDirection={!isMobileMode ? 'column' : 'row'}>
          <Typography className={cx(classes.headerText, classes.totalItems)}>
            Total Ordered:
          </Typography>
          <Typography className={cx(classes.text, classes.totalItems)}>
            {getTotalOrderedQty(order, productsObj)}
          </Typography>
        </Box>
      </Grid>
      {!isMobileMode && (
        <Grid
          item
          flexBasis={gridPropValues({
            lgSize: 3.7,
            mdSize: 6,
            unit: '%',
          })}
          container
          pr={2.5}
          alignContent={'end'}
          justifyContent={'right'}>
          <EditRecurrenceButton onClick={onEditRecurrence} />
        </Grid>
      )}
    </Grid>
  );
};

export default RecurringOrderCard;
